import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { X, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { PurchaseFilterField, PurchaseFilterTag, PurchaseFilterOperator } from "@/hooks/useAdminCampaignPurchases";

interface CampaignPurchasesFilterProps {
  filterTags: PurchaseFilterTag[];
  onAddFilter: (field: PurchaseFilterField, value: string, operator?: PurchaseFilterOperator) => void;
  onRemoveFilter: (id: string) => void;
  onClearAllFilters: () => void;
}

const FIELD_OPTIONS: { 
  value: PurchaseFilterField; 
  label: string; 
  placeholder: string;
  operators?: PurchaseFilterOperator[];
}[] = [
  { value: "campaign", label: "Campaign", placeholder: "Campaign name...", operators: ["contains", "equals"] },
  { value: "package", label: "Package", placeholder: "Package name...", operators: ["contains", "equals"] },
  { value: "name", label: "Buyer", placeholder: "Buyer name...", operators: ["contains", "equals"] },
  { value: "email", label: "Email", placeholder: "Email address...", operators: ["contains", "equals"] },
  { value: "userId", label: "User ID", placeholder: "User ID...", operators: ["contains", "equals"] },
  { value: "pricePaid", label: "Price Paid", placeholder: "Enter amount...", operators: ["equals", ">=", "<=", ">", "<"] },
  { value: "creditsGranted", label: "Credits", placeholder: "Enter amount...", operators: ["equals", ">=", "<=", ">", "<"] },
];

const FIELD_COLORS: Record<PurchaseFilterField, string> = {
  campaign: "bg-blue-500/10 text-blue-700 border-blue-200",
  package: "bg-purple-500/10 text-purple-700 border-purple-200",
  name: "bg-green-500/10 text-green-700 border-green-200",
  email: "bg-orange-500/10 text-orange-700 border-orange-200",
  userId: "bg-slate-500/10 text-slate-700 border-slate-200",
  pricePaid: "bg-yellow-500/10 text-yellow-700 border-yellow-200",
  creditsGranted: "bg-cyan-500/10 text-cyan-700 border-cyan-200",
};

const OPERATOR_LABELS: Record<PurchaseFilterOperator, string> = {
  contains: "contains",
  equals: "=",
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
};

const CampaignPurchasesFilter: React.FC<CampaignPurchasesFilterProps> = ({
  filterTags,
  onAddFilter,
  onRemoveFilter,
  onClearAllFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<PurchaseFilterField>("campaign");
  const [selectedOperator, setSelectedOperator] = useState<PurchaseFilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");

  const currentFieldConfig = FIELD_OPTIONS.find(f => f.value === selectedField);
  const hasOperators = !!currentFieldConfig?.operators && currentFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (!inputValue.trim()) return;
    const operator = hasOperators ? selectedOperator : undefined;
    onAddFilter(selectedField, inputValue.trim(), operator);
    setInputValue("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: PurchaseFilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: PurchaseFilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {currentFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              <div className="flex gap-2">
                <Input
                  placeholder={currentFieldConfig?.placeholder || "Enter value..."}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-8 text-sm"
                  type={["pricePaid", "creditsGranted"].includes(selectedField) ? "number" : "text"}
                />
                <Button size="sm" className="h-8" onClick={handleAddFilter}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {filterTags.map((tag) => (
        <div
          key={tag.id}
          className={cn(
            "flex items-center gap-1 h-7 px-2 rounded-md border text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.label}:</span>
          <span>{formatTagDisplay(tag)}</span>
          <button
            onClick={() => onRemoveFilter(tag.id)}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={onClearAllFilters}
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default CampaignPurchasesFilter;
